<template>
  <div class="start-for-free-page">
    <StartLearningToday class="start-learn-today" class-name="start-your-journey-for-free" event-prefix="start_your_journey_hero_">
      <div class="rated">
        <span> Rated as Excellent on </span>
        <IconTrustPilot />
      </div>
      <h1 class="h1">
        {{ $config.platform === 'fa' ? 'Start Learning Finance Online for' : 'Start Learning Data Science Online for' }}
        <span>Free
          <IconTextDecoration />
        </span>
      </h1>
      <p class="subtitle">
        {{ $config.platform === 'fa' ?
          ' Gain the skills you need to succeed in today’s competitive world of finance. Take the first step toward your dream job with free access to:'
          : 'Gain the skills you need to succeed in today’s data-driven business environment. Make the first step with free access to:' }}
      </p>
      <ul>
        <li>{{ stats.rounded.freeVideoDuration }}+ hours of video lessons</li>
        <li>{{ stats.rounded.freeExercises }}+ practical exercises</li>
        <li>Downloadable materials</li>
        <li>In-platform resume builder</li>
        <li>Gamified features</li>
      </ul>
      <div class="footer">
        <IconCreditCard />
        <span> No credit card required </span>
      </div>
    </StartLearningToday>
    <div v-if="$config.platform === 'ds'" class="site-section learn-data-science" :class="{ 'gray': $store.state.layout === 'mobile' }">
      <div class="container player-container">
        <div class="player-section">
          <div class="player-heading">
            <h2 class="h3 col-8">
              Discover a Structured Online Program
            </h2>
          </div>
          <div class="col-10 offset-1">
            <VideoPlayer
              v-if="videoJSLoaded" slot="player" :video-id="videoSource"
              class="video-player"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="site-section gray learn-data-science">
      <InstructorSlider
        v-if="$config.platform === 'ds'"
        :instructors="instructors"
        :options="instructorsSliderOptions"
      />
      <div v-if="$config.platform === 'ds'" class="container">
        <div class="row">
          <div class="col col-12">
            <div class="university-logos">
              <div class="title">
                <h4 class="h4 col-8">
                  You will learn data science with world-class instructors who hold degrees from some of the most prestigious universities.
                </h4>
              </div>
              <div class="logos-holder">
                <div v-for="(logo, i) in universityLogos" :key="i" class="logo">
                  <img :src="require(`../../assets/svg/universities/logo-${logo}.svg`)" :alt="`${logo}-logo`">
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <DataScienceCards :options="$config.platform === 'ds' ? dataScienceCardsOptions : financialAnalystCardsOptions" button-text="Start Learning for Free" @scrollToLastSection="scrollToLastSection" />
    </div>
    <div class="site-section dark">
      <Curriculum
        v-if="$config.platform === 'ds'"
        use-free-stats
        :stats-hardcoded="stats.rounded"
        :subtitle="curriculumSubtitle"
      />
      <ProgramCards
        v-else
        title="Travel Down an Expert-Led Learning Path"
        subtitle="Learn new skills to advance your finance career. Our comprehensive training is perfect both for complete beginners and working professionals who want to add in-demand skills and tools to their resumes. Start with the free lessons from each course and see if the program is a good fit for you."
      />
      <div v-if="$config.platform === 'fa'" class="container footer-icons">
        <div>
          <ClockIcon class="icon-courses" />
          <div class="description">
            <span>{{ stats.rounded.freeVideoDuration }}+</span>
            Hours of Video
          </div>
        </div>
        <div>
          <IconMonitorPlay class="icon-exercises" />
          <div class="description">
            <span>{{ stats.rounded.freeExercises }}+</span>
            Exercises
          </div>
        </div>
        <div>
          <IconPeople class="icon-students" />
          <div class="description">
            <span>{{ $c.text('students.count') }}+</span>
            Students
          </div>
        </div>
      </div>
    </div>
    <PreFooter
      :title="$config.platform === 'ds' ? 'Start Your Data Science Journey Today' : 'Start Working Toward Your Goals Today'"
      :description="$config.platform === 'ds' ? 'Take the first step and prepare for your exciting future career.' : 'Take the first step and prepare for your ideal future career.'"
      button-title="Sign up for Free"
      button-link=""
      :columns="$config.platform === 'ds' ? 'col-6' : 'col-8'"
      :background="$config.platform === 'ds' ? false : true"
      bg-shadow-hide
      event-prefix="start_your_journey_"
      @scroll-to-last-section="scrollToLastSection"
    />
    <div class="student-reviews site-section dark">
      <div class="container">
        <div class="row">
          <div class="col col-5">
            <div class="slider-container-reviews">
              <div class="slider-header">
                <div class="heading">
                  <div>
                    <h2 class="h2">
                      Trusted by Over 3,000,000 Students
                    </h2>
                    <img
                      v-if="$config.platform === 'ds'"
                      loading="lazy"
                      class="mobile-image"
                      src="../../assets/images/students.png"
                      alt="Students"
                    >
                    <img
                      v-else class="mobile-image"
                      loading="lazy"
                      src="../../assets/images/fa/fa-landing/fa-students-reviews.png"
                      alt="Students"
                    >
                    <IconQuotationMark class="quotation-marks" />
                  </div>
                </div>
              </div>
              <div
                v-swipe.right="prevSlide" v-swipe.left="nextSlide" class="swiper"
                :style="{ transform: `translateX(${transform}px)` }"
              >
                <div
                  v-for="(student, i) in students"
                  :key="i"
                  class="card-review"
                >
                  <div class="student">
                    <p>{{ students[i].text }}</p>
                    <span class="name">{{ students[i].name }}</span>
                    <span class="body-2">{{ students[i].country }}</span>
                  </div>
                </div>
              </div>
              <div v-if="!(transform === 0 && onLast)" class="controls">
                <CaretLeftCircle
                  :class="{ disabled: transform === 0 }"
                  @click="prevSlide"
                />
                <CaretRightCircle
                  :class="{ disabled: onLast }"
                  @click="nextSlide"
                />
              </div>
            </div>
          </div>
          <div class="col col-6 offset-1 image-container">
            <img
              v-if="$config.platform === 'ds'"
              loading="lazy"
              src="../../assets/images/students.png"
              alt="students"
            >
            <img
              v-else class="mobile-image"
              loading="lazy"
              src="../../assets/images/fa/fa-landing/fa-students-reviews.png"
              alt="students"
            >
          </div>
        </div>
      </div>
    </div>
    <div class="site-section gray">
      <Reviews />
    </div>
    <StartLearningToday
      id="start-learn-today" class="start-learn-today prefooter" class-name="start-your-journey-for-free prefooter"
      unique-id="prefooter"
      event-prefix="start_your_journey_footer_"
    >
      <h2>
        {{ $config.platform === 'ds' ? 'Access Your Free Data Science Lessons Today' : 'Access Your Free Finance Lessons Today' }}
      </h2>
      <p class="body-1">
        {{ $config.platform === 'ds' ? 'Enjoy a variety of data science topics and start building the key skills for your future career.' : 'Choose from a variety of finance topics and start building the key skills for your future career.' }}
      </p>
      <ul>
        <li class="body-1">
          World-class instructors
        </li>
        <li class="body-1">
          Self-paced video lessons
        </li>
        <li class="body-1">
          Practical exercises
        </li>
        <li class="body-1">
          Downloadables
        </li>
        <li class="body-1">
          Gamified features
        </li>
      </ul>
      <div class="footer">
        <IconCreditCard />
        <span> No credit card required </span>
      </div>
    </StartLearningToday>
  </div>
</template>

<script>
import VideoPlayer from '@itbpbg/lms-components/src/components/VideoPlayer.vue';
import CaretLeftCircle from '@itbpbg/lms-components/src/assets/svg/caret-left-circle.svg?vue-component';
import CaretRightCircle from '@itbpbg/lms-components/src/assets/svg/caret-right-circle.svg?vue-component';
import ClockIcon from '@itbpbg/lms-components/src/assets/svg/courses/clock-colored.svg?vue-component';
import StartLearningToday from '../../components/StartLearningToday';
import InstructorSlider from '../../components/Landing/InstructorSlider';
import Curriculum from '../../components/Landing/Curriculum';
import Reviews from '../../components/Landing/Reviews';
import DataScienceCards from '../../components/Landing/DataScienceCards';
import ProgramCards from '../../components/Landing/ProgramCards';
import PreFooter from '../../components/PreFooter';
import IconTrustPilot from '../../assets/svg/icon-trustpilot.svg?vue-component';
import IconCreditCard from '../../assets/svg/icon-credit-card.svg?vue-component';
import IconQuotationMark from '../../assets/svg/icon-quotation-marks.svg?vue-components';
import IconTextDecoration from '../../assets/svg/icon-text-decoration.svg?vue-component';
import IconPeople from '../../assets/svg/icon-blog-meet-the-team.svg?vue-components';
import IconMonitorPlay from '../../assets/svg/icon-monitor-play.svg?vue-components';

export default {
  components: {
    VideoPlayer,
    StartLearningToday,
    InstructorSlider,
    Curriculum,
    Reviews,
    PreFooter,
    IconTrustPilot,
    IconCreditCard,
    DataScienceCards,
    ProgramCards,
    IconQuotationMark,
    CaretLeftCircle,
    CaretRightCircle,
    IconTextDecoration,
    IconPeople,
    IconMonitorPlay,
    ClockIcon,
  },
  props: {
    instructors: {
      type: Array,
      default: () => [],
    },
    stats: {
      type: Object,
      default: () => ({}),
    },
    page: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      videoSource: '6297413182001',
      videoJSLoaded: false,
      instructorsSliderOptions: {
        title: 'A Comprehensive Training Led by Renowned Industry Experts',
        className: 'landing',
      },
      universityLogos: ['cambridge', 'oxford', 'edinburgh', 'caltech', 'bocconi', 'texas'],
      students: [...(
        this.$config.platform === 'ds' ? [
          {
            text: 'The Data Science world is overwhelming if you are a total beginner. I just have medium-advanced knowledge in Excel and the intro to Data and Data Science Module is really great. Thoroughly explained, made simple, and giving me a more holistic insight of the Data world. Great course.',
            name: 'José Maria Robles Alvarez',
            country: 'Spain',
          },
          {
            text: 'The 365 Data Science program is as good as it gets. It builds your concepts from scratch all the way to a professional level. The program is rigorous in both its explanations and the exercises. The instructors are well-experienced, and th...',
            name: 'DivS',
            country: 'Belgium',
          },
          {
            text: '365 Data Science is really great for learning essential skills and knowledge in every aspect of data science. The time for every session is short and you can easily complete it, and continue to learn or solve the exercises.',
            name: 'mahsa choop',
            country: 'United States',
          },
        ] : [
          {
            text: 'I didn\'t know anything about Investment Banking when I started this course. The course is structured in a very interesting and engaging way, that makes it easy to understand, and easy to remember. I highly recommend taking it as I now have a brilliant understanding of the Banking world.',
            name: 'Kian P.',
            country: 'Germany',
          },
          {
            text: 'The course was very informative and engaging. Definitely applaud the tutor for his great effort in explaining everything so clearly and simply. I\'ve become more interested in corporate finance since starting this course. Highly recommended!',
            name: 'Trang',
            country: 'Pakistan',
          },
          {
            text: 'Great course. Starts from the basics for those with little or no experience but ends up going pretty deep in the concepts. Particularly liked the valuation and modeling part. It is hard to find courses that actually teach how to model an LBO.',
            name: 'Rodrigo Leitao Rodrigues D.',
            country: 'Columbia',
          },
        ]),
      ],
      dataScienceCardsOptions: {
        title: 'The Best Way to Learn Data Science',
        subtitle: 'Our beginner-to-advanced training starts from the fundamentals and builds up your expertise in a logical and easy-to-follow way. Step by step, no prior experience required.',
        firstCardTitle: 'Career Tracks',
        firstCardText: 'Explore the first sections of structured career tracks designed to develop the unique skillset for your target job role – Data Scientist, Data Analyst, or Business Analyst.',
        secondCardTitle: 'Self-paced Online Courses',
        secondCardText: 'Access your video lessons and exercises anytime, anywhere. Set your daily learning goals and make success a routine.',
        thirdCardTitle: 'Taught by Outstanding Professionals',
        thirdCardText: 'Get familiar with the most in-demand tools and techniques under the guidance of the best experts in the field. Receive personalized assistance throughout your learning process.',
      },
      financialAnalystCardsOptions: {
        title: 'The Best Way to Enhance Your Finance Skills',
        subtitle: 'Upskill through a myriad of practical examples and exercises. Our beginner-to-advanced training offers engaging and easy-to-follow lessons suitable for all levels of experience.',
        firstCardTitle: 'Structured Career Tracks',
        firstCardText: 'You set the path that best suits your professional goals. Master highly sought-after skills, pick the electives to make you stand out, ace the exams, and advance your finance career.',
        secondCardTitle: 'Self-Paced Online Courses',
        secondCardText: 'Access your video lessons, exercises, and real-world business cases anytime, anywhere. Set your daily learning goals and make success a routine.',
        thirdCardTitle: 'In-Platform Resume Builder',
        thirdCardText: 'Our in-platform resume builder helps you write a sharp and effective resume in minutes and connect with high-profile employers from our list of partner companies.',
      },
      curriculumSubtitle: 'Learn data science from the ground up. From the very basics to the most advanced topics, our all-in-one training is a perfect solution both for complete beginners and working professionals who want to add in-demand skills and technologies to their toolbox. Get started with the free lessons from each module and see if the program is the right fit for you.',
      onLast: false,
      transform: 0,
      containerWidth: 0,
    };
  },
  async mounted() {
    const eventID = `${this.$store.state.tracking.sessionId}${Date.now()}`;
    dataLayer.push({ event: 'LandingPageView', eventID });
    this.$tracking.sendEvent({
      dataLayerEvent: 'ViewLandingPage',
      destinationUrl: this.$router.currentRoute.fullPath,
      eventID,
      capi: true,
      userAgent: navigator.userAgent,
    });
    await this.$pollValue(() => typeof videojs !== 'undefined');
    this.videoJSLoaded = true;
    this.init();

    window.addEventListener('resize', this.init);
  },
  methods: {
    init() {
      this.containerWidth = this.$el.querySelector('.slider-container-reviews').offsetWidth;
      this.cardWidth = this.$el.querySelector('.card-review').offsetWidth;

      if (this.$el.querySelectorAll('.card-review').length * this.cardWidth - 30 <= this.containerWidth) {
        this.onLast = true;
      }
    },
    nextSlide() {
      const lastCard = this.$el.querySelector('.card-review:last-child').getBoundingClientRect();
      const totalCards = this.$el.querySelectorAll('.card-review').length * this.cardWidth;

      if (!this.onLast) {
        if (this.containerWidth - totalCards <= this.transform + -this.cardWidth + 24) {
          this.transform += -this.cardWidth;
        }

        if (lastCard.left + 12 < this.containerWidth + this.cardWidth) {
          this.onLast = true;
        }
      }
    },
    prevSlide() {
      if (this.transform + this.cardWidth <= 0) {
        this.transform -= -this.cardWidth;
        this.onLast = false;
      }
    },
    scrollToLastSection() {
      const signUpToContinue = document.querySelector('.start-learn-today.prefooter');
      signUpToContinue.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    },
  },
};
</script>

<style lang="scss">
.start-for-free-page {
  .start-your-journey-for-free {
    .signup-form-wrapper {
      .form-wrap {
        padding: var(--spacing-44);

        .header h2 {
          margin-bottom: var(--spacing-24);
        }

        .divider {
          padding: 33px 0;
        }

        .input-group > div label {
          position: relative;
        }
      }
    }

    .rated {
      margin-bottom: var(--spacing-16);

      span {
        font-size: 18px;
        color: var(--main-dark-shade-4);
      }

      svg {
        width: 98px;
        height: 24px;
        margin-left: 15px;
      }

      span,
      svg {
        vertical-align: middle;
        display: inline-block;
      }
    }

    .h1 {
      font-size: 48px;

      span {
        color: var(--accent);
        position: relative;

        svg {
          position: absolute;
          bottom: -6px;
          left: -8px;
          max-width: 100%;
        }
      }
    }

    .subtitle {
      color: var(--main-dark-shade-4);
    }

    .footer {
      margin-top: var(--spacing-32);

      span,
      svg {
        vertical-align: middle;
        display: inline-block;
      }

      svg {
        margin-right: var(--spacing-16);
      }

      span {
        font-size: 18px;
      }
    }
  }

  .player-container {
    .player-section .player-heading {
      text-align: center;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: var(--spacing-32);

      .h3 {
        font-size: 40px;
        width: 100%;
        margin: 0;
      }
    }

    .iframe-video {
      position: relative;
      width: 100%;
      height: 0;
      padding-bottom: 46.85%;

      iframe {
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        border-radius: 10px;
      }
    }

    @include bp($bp-mobile) {
      padding: 0;

      .player-section {
        .player-heading {
          padding-left: var(--spacing-24);
          padding-right: var(--spacing-24);

          .h3 {
            font-size: 28px;
          }
        }

        .iframe-video {
          padding-bottom: 56.25%;

          iframe {
            border-radius: 0;
          }
        }
      }
    }
  }

  .site-section {
    padding-top: var(--spacing-80);
    padding-bottom: var(--spacing-80);

    &.gray {
      background-color: var(--main-light-shade-1);
    }

    &.reviews-wrapper {
      padding-top: 120px;
      padding-bottom: 120px;
    }

    .reviews-trustpilot {
      margin: 0 -15px;

      .reviews-body {
        padding: 0 15px;

        &.show {
          padding-bottom: var(--spacing-8);
        }
      }
    }

    .col {
      float: unset;
    }
  }

  .pre-footer {
    padding-top: var(--spacing-120);

    .bg {
      margin-top: var(--spacing-120);
    }
  }

  .learn-data-science {
    .university-logos {
      padding-top: 140px;
      padding-bottom: 140px;

      .title {
        display: flex;
        justify-content: center;
        margin-bottom: var(--spacing-48);

        .h4 {
          font-size: 21px;
          line-height: 140%;
          font-weight: normal;
          text-align: center;
          margin: 0;
        }
      }

      .logos-holder {
        display: flex;
        justify-content: space-between;

        .logo {
          height: 96px;
          width: 210px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--main-light);
          box-shadow: 0 16px 30px rgba(0, 12, 31, 0.04);
          border-radius: 7px;

          img {
            width: 70%;
            margin: 0 auto;
          }

          &:nth-of-type(4) {
            display: none;
          }
        }
      }
    }

    .data-science-cards {
      .cards {
        .card {
          margin-top: 0;
          padding: 52px 30px;
          height: 100%;

          .h4 {
            font-size: 24px;
            line-height: 26px;
          }

          span {
            line-height: 140%;
          }
        }
      }

      @include bp($bp-mobile) {
        .heading {
          span {
            color: var(--main-dark-shade-0);
          }
        }

        .card {
          margin-bottom: var(--spacing-28);
        }
      }
    }
  }

  .curriculum {
    .heading {
      .h2 {
        max-width: 90%;
      }

      span {
        max-width: 83%;
      }
    }

    .modules {
      .module {
        .h3 {
          max-width: 100%;
        }

        .body-2 {
          max-width: 724px;
        }
      }
    }

    @include bp($bp-mobile) {
      .heading {
        .h2,
        span {
          max-width: 100%;
        }
      }
    }
  }

  .student-reviews {
    .container .row > div:first-child {
      .h2 {
        margin-bottom: var(--spacing-48);
      }

      .quotation-marks {
        width: 44px;
      }

      .student {
        margin-top: var(--spacing-24);
        margin-bottom: var(--spacing-36);

        p {
          font-size: 18px;
          margin-bottom: var(--spacing-24);
          color: var(--main-light-shade-4);
        }

        .name {
          display: block;
          font-weight: 500;
        }

        .body-2 {
          color: var(--main-dark-shade-3);
        }
      }

      .controls {
        svg {
          cursor: pointer;

          &:first-of-type {
            margin-right: 14px;
          }

          &.disabled {
            circle {
              stroke: #4C5462;
            }

            path[stroke] {
              stroke: #4C5462;
            }
          }

          circle {
            fill: transparent;
            stroke: var(--main-light);
          }

          path[stroke] {
            stroke: var(--main-light);
          }
        }
      }
    }

    .slider-container-reviews {
      overflow: hidden;

      .swiper {
        justify-items: stretch;
        transform: translateX(0);
        transition: transform 1s ease-out;
        flex-wrap: nowrap;
        display: flex;

        .card-review {
          width: 100%;
          flex: 0 0 auto;
        }
      }

      .slider-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 36px;

        .mobile-image {
          display: none;
        }
      }
    }

    .image-container {
      text-align: right;

      img {
        width: 100%;
        margin-left: auto;
      }
    }

    @include bp($bp-mobile) {
      .slider-container-reviews {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;

        .slider-header {
          .heading div {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            text-align: center;

            .h2 {
              font-size: 28px;
            }

            .mobile-image {
              display: block;
              max-width: 100%;
              margin-bottom: var(--spacing-40);
            }
          }
        }

        .swiper {
          .card-review {
            text-align: center;
          }
        }
      }

      .image-container {
        display: none;
      }
    }
  }

  .start-learn-today.prefooter {
    position: relative;

    &:before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: var(--main-dark);
      opacity: 0.6;
      z-index: 0;
    }

    h2:not(.h4) {
      font-size: 44px;
      margin-bottom: var(--spacing-48);
    }

    p.body-1 {
      margin-bottom: var(--spacing-16);
    }

    ul li {
      margin-bottom: var(--spacing-12);
    }

    .footer {
      margin-top: var(--spacing-48);
      color: var(--main-dark-shade-4);

      span,
      svg {
        vertical-align: middle;
        display: inline-block;
      }

      svg {
        margin-right: var(--spacing-16);

        & path {
          stroke: var(--main-dark-shade-4);
          fill: var(--main-dark-shade-4);
        }
      }
    }
  }

  @media screen and (max-width: 370px) {
    .window.start-your-journey-for-free .content > div .h1 {
      img {
        display: none;
      }
    }
  }
}

@include bp($bp-laptop) {
  .start-for-free-page {
    .start-your-journey-for-free {
      .content {
        h1 {
          font-size: 40px;
          margin-bottom: var(--spacing-32);
        }

        .subtitle {
          margin-bottom: var(--spacing-28);
        }

        ul {
          line-height: 200%;

          li {
            margin-bottom: 0;
          }
        }
      }

      .signup-form-wrapper {
        padding-right: 15px;
      }
    }

    .player-container .player-section .player-heading {
      .h3 {
        font-size: 32px;
      }
    }

    .slider-container.landing .slider-header .heading h2 {
      font-size: 32px;
    }

    .data-science-cards {
      padding-bottom: var(--spacing-40);

      .cards {
        margin-top: var(--spacing-28);
      }
    }

    .start-learn-today.prefooter {
      h2:not(.h4) {
        font-size: 32px;
      }

      ul li {
        margin-bottom: var(--spacing-12);
      }
    }

    .learn-data-science {
      .university-logos {
        .logos-holder {
          flex-wrap: wrap;

          .logo {
            height: 81px;
            width: 178px;
          }
        }
      }
    }
  }
}

@include bp($bp-mobile) {
  .start-for-free-page {
    .window.start-your-journey-for-free .signup-form-wrapper {
      padding-left: 0;
      padding-right: 0;

      .form-wrap {
        padding: 0;
      }
    }

    .player-container {
      .video-player {
        border-radius: 0;
      }
    }

    .pre-footer {
      padding-top: var(--spacing-80);

      .content h3 {
        font-size: 32px;
      }

      .bg {
        margin-top: var(--spacing-80);
      }
    }

    .curriculum {
      .modules {
        .module {
          &.show {
            padding: var(--spacing-32) var(--spacing-32) var(--spacing-20);
            max-height: 789px;

            .module-see-more {
              bottom: 0;
              line-height: 22px;
              padding: var(--spacing-16) 0 var(--spacing-20);
              background-color: var(--main-dark-shade-1);
            }

            &.expand {
              max-height: 1600px;
            }
          }
        }
      }
    }

    .learn-data-science .university-logos {
      .logos-holder {
        flex-wrap: wrap;
        justify-content: center;
        row-gap: 14px;

        .logo {
          margin: 0 var(--spacing-14);
        }
      }
    }
  }
}

@include bp($bp-phone) {
  .start-for-free-page {
    .window.start-your-journey-for-free {
      .content > div {
        padding-bottom: 68px;
      }

      .signup-form-wrapper {
        padding: var(--spacing-60) var(--spacing-24);

        .form-wrap {
          padding: 0;
        }
      }
    }

    .pre-footer {
      .content h3 {
        font-size: 28px;
      }
    }

    .learn-data-science .university-logos {
      .title .h4 {
        font-size: 21px;
      }

      .logos-holder {
        display: block;
        margin: 0 auto;
        width: auto;

        .logo {
          width: calc((100% - 30px) / 2);
          height: 70px;
          display: flex;
          align-items: center;
          margin-bottom: var(--spacing-14);

          img {
            width: 80%;
          }

          &:nth-of-type(4) {
            display: flex;
          }

          &:nth-child(2n +1) {
            margin-left: 14px;
            float: left;
          }

          &:nth-child(3) {
            margin-left: 0;
          }

          &:nth-child(2n) {
            margin-right: 0;
          }
        }
      }
    }
  }
}

.fa {
  @import '../../assets/sass/fa/pages/start-your-journey-for-free.scss';
}
</style>
